import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Calendar, Users, Newspaper, Globe, Mail, FileSpreadsheet, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    icon: Calendar,
    title: "Manage events",
    steps: [
      "Open Admin → Events tab.",
      "Click ‘Create new event’ to add a title, date, format (in-person / hybrid / online), capacity and description.",
      "To change an existing event, hit the pencil icon on its row, the form opens pre-filled.",
      "Members RSVP via the public Events page; your dashboard shows live counts.",
    ],
  },
  {
    icon: Users,
    title: "Add or import members",
    steps: [
      "Members register themselves at /register or join free at /membership.",
      "To bulk-add (e.g. an old WhatsApp list) prepare a CSV with columns: email,name,city,phone (only email is required).",
      "On the Members tab click ‘Import CSV’ and pick the file. Existing emails are skipped automatically.",
      "Each new row is auto-assigned a sequential membership ID like ABSUK-2026-0014.",
    ],
  },
  {
    icon: FileSpreadsheet,
    title: "Export your data",
    steps: [
      "Every Admin tab has a Download button on the top-right.",
      "Members, Events, Donations and Messages each export to CSV, open in Excel or Google Sheets.",
      "Use exports for AGM reports, Charity Commission filings, or backups before site changes.",
    ],
  },
  {
    icon: Newspaper,
    title: "Compose a newsletter (broadsheet edition)",
    steps: [
      "Open Admin → Newsletter tab.",
      "Click ‘New edition’. Fill in: Title (eg ‘Buddha Jayanti Special’), Edition label (‘Vol 1 · No 1 · Feb 2026’), Issue date, optional Editorial lead.",
      "Add articles one by one, each article has Headline, Kicker (small label above), Byline, Standfirst (dek), and Body. Separate paragraphs with a blank line.",
      "Save as Draft to preview, then change Status to ‘Published’ when ready. The /newsletter page picks it up immediately.",
      "Hit ‘Send to all members’ to email every registered member. (Resend is currently mocked, emails are logged until your API key is added.)",
    ],
  },
  {
    icon: Globe,
    title: "11-language switcher",
    steps: [
      "Visitors choose their language from the globe icon in the header.",
      "Translations are produced live by AI (Gemini 2.5) and cached in your database, so each phrase is paid-for only once.",
      "Buddhist/Ambedkarite terms (Dhamma, Babasaheb, Mettā, Sangha, etc.) are preserved exactly, they are never translated.",
      "Languages: English, Hindi, Marathi, Tamil, Telugu, Kannada, Malayalam, Bengali, Gujarati, Punjabi, Urdu.",
    ],
  },
  {
    icon: Mail,
    title: "Activate real emails (Resend)",
    steps: [
      "Sign up free at https://resend.com, they give 3,000 emails/month free.",
      "Verify the domain abs-uk.org (DNS records) or send from their default test domain.",
      "Tell us your API key (re_…), we’ll set RESEND_API_KEY in the backend .env, and welcome / newsletter emails go out for real.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Membership & ID cards",
    steps: [
      "Three tiers: Community (free), Annual £24, Lifetime £250.",
      "Each member has a digital card at /profile/card with QR code → /verify/<membership-id>.",
      "Voting eligibility kicks in 90 days after a paid membership starts (Lifetime always eligible).",
      "Stripe is in test mode, use card 4242 4242 4242 4242 to try it without spending real money.",
    ],
  },
];

export default function AdminHelp() {
  return (
    <section className="py-16 bg-pearl min-h-[calc(100vh-5rem)]" data-testid="admin-help-page">
      <div className="section-container">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Help & guidance</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl text-navy">Running ABGN-UK on your own</h1>
            <p className="mt-3 max-w-2xl text-navy/75 leading-relaxed">
              A simple, jargon-free guide to managing the website day-to-day, events, members, newsletters, exports
              and the multi-language switcher.
            </p>
          </div>
          <Link
            to="/admin"
            data-testid="admin-help-back"
            className="px-5 py-2.5 border border-navy/20 text-navy text-sm hover:bg-navy hover:text-pearl transition-colors"
          >
            ← Back to Admin
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`admin-help-section-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
                className="bg-white border border-navy/10 p-6 sm:p-8 rounded-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-navy/5 text-saffron">
                    <Icon size={22} />
                  </div>
                  <h2 className="font-display text-2xl text-navy">{s.title}</h2>
                </div>
                <ol className="list-decimal list-outside ml-5 space-y-2 text-sm text-navy/85 leading-relaxed">
                  {s.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-navy text-pearl/90 p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="text-gold" size={22} />
            <h3 className="font-display text-2xl text-pearl">Anything else you’d like a guide for?</h3>
          </div>
          <p className="text-pearl/75 max-w-2xl">
            Tell us in the Contact page or message the developer team, we’ll write a step-by-step section for it
            and add it here. The aim is for any committee member, not just a tech-savvy one, to be able to run the
            site confidently.
          </p>
        </div>
      </div>
    </section>
  );
}
